import type { ActiveAlert, OverviewPool, SeriesPoint } from '../../shared/types';
import { fmtCompact, fmtPct, fmtSignedPct, mean } from '../../shared/format';
import './../styles/components/hero.css';

const DAY = 86400;

type Agg = { now: number; then: number };

function nearest(series: SeriesPoint[], ts: number, tol: number): SeriesPoint | null {
  let best: SeriesPoint | null = null;
  for (const p of series) {
    if (Math.abs(p.ts - ts) > tol) continue;
    if (!best || Math.abs(p.ts - ts) < Math.abs(best.ts - ts)) best = p;
  }
  return best;
}

/** BOLD pools count at the BOLD price; USDC at par. */
const usd = (v: OverviewPool, amount: number | null) =>
  (amount ?? 0) * (v.pool.asset === 'BOLD' ? v.latest?.price ?? 1 : 1);

/** Exit liquidity now vs `ago` seconds back, over the venues that have both samples. */
function aggregate(venues: OverviewPool[], ago: number): Agg | null {
  let now = 0;
  let then = 0;
  let n = 0;
  for (const v of venues) {
    const cur = v.latest;
    if (!cur || cur.availableLiquidity === null) continue;
    const base = nearest(v.series, cur.ts - ago, 2 * 3600);
    if (!base || base.availableLiquidity === null) continue;
    now += usd(v, cur.availableLiquidity);
    then += usd(v, base.availableLiquidity);
    n++;
  }
  return n > 0 && then > 0 ? { now, then } : null;
}

/** `pools` are the held venues; `watched` the watch-only ones (only used for the all-venue APY). */
type Props = { pools: OverviewPool[]; watched: OverviewPool[]; alerts: ActiveAlert[]; gasGwei: number | null; gasWarn: boolean };

const apyOf = (venues: OverviewPool[]) => mean(venues.map((v) => v.latest?.supplyApy ?? null));

export function Hero({ pools, watched, alerts, gasGwei, gasWarn }: Props) {
  const venues = pools.filter((p) => p.pool.kind !== 'eth-gas');
  const total = venues.reduce((n, v) => n + usd(v, v.latest?.availableLiquidity ?? null), 0);
  const critical = alerts.filter((a) => a.severity === 'critical').length;
  const warn = alerts.length - critical;
  const heldApy = apyOf(venues);
  const allApy = apyOf([...venues, ...watched]);
  const alertText = alerts.length === 0
    ? 'All clear'
    : [critical && `${critical} critical`, warn && `${warn} warn`].filter(Boolean).join(' · ');

  return (
    <section class="hero">
      <div class="hero-label mono">Exit liquidity · {venues.length} venues</div>
      <div class="hero-value mono">${fmtCompact(total)}</div>
      <dl class="hero-stats">
        <Change label="Last 24h" agg={aggregate(venues, DAY)} />
        <Change label="Last 7d" agg={aggregate(venues, 7 * DAY)} />
        {heldApy !== null && (
          <div class="hero-stat">
            <dt class="hero-stat-label mono">Avg APY</dt>
            <dd class="hero-stat-value mono">
              {fmtPct(heldApy, 2)}
              {watched.length > 0 && <span class="hero-stat-sub">&nbsp;· all {venues.length + watched.length} venues {fmtPct(allApy, 2)}</span>}
            </dd>
          </div>
        )}
        <div class="hero-stat">
          <dt class="hero-stat-label mono">Active alerts</dt>
          <dd class={`hero-stat-value mono ${critical ? 'loss' : warn ? 'is-warn' : 'gain'}`}>{alertText}</dd>
        </div>
        {gasGwei !== null && (
          <div class="hero-stat">
            <dt class="hero-stat-label mono">Base fee</dt>
            <dd class={`hero-stat-value mono${gasWarn ? ' is-warn' : ''}`}>
              {gasGwei < 10 ? gasGwei.toFixed(2) : gasGwei.toFixed(0)}
              <span class="hero-stat-sub">&nbsp;gwei</span>
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
}

function Change({ label, agg }: { label: string; agg: Agg | null }) {
  const pct = agg ? agg.now / agg.then - 1 : null;
  const cls = pct === null ? 'flat' : pct >= 0 ? 'gain' : 'loss';
  return (
    <div class="hero-stat">
      <dt class="hero-stat-label mono">{label}</dt>
      <dd class="hero-stat-body">
        <span class={`hero-pill ${cls}`}>
          {agg && <span>{(agg.now >= agg.then ? '+' : '−') + fmtCompact(Math.abs(agg.now - agg.then))}</span>}
          <span>{fmtSignedPct(pct)}</span>
        </span>
      </dd>
    </div>
  );
}
